import { lazy, ComponentType } from 'react';

interface Route {
  path: string;
  component: ComponentType;
  componentName: string;
}

/**
 * Route Registry
 *
 * Manually register component routes here.
 * Each route uses React.lazy() for code splitting.
 *
 * To add a new component:
 * 1. Import it using lazy()
 * 2. Add an entry to the routes array with the path and component
 */

// Lazy-loaded component routes
const DeskInfoRoute = lazy(() =>
  import('@/components/BookingActions/DeskInfo').then(({ DeskInfo }) => ({
    default: () => <DeskInfo />,
  }))
);

const BookingSuggestionsRoute = lazy(() =>
  import('@/components/BookingSuggestions/BookingSuggestions').then(({ BookingSuggestions }) => ({
    default: () => <BookingSuggestions />,
  }))
);

const CustomResourceInfoRoute = lazy(() =>
  import('@/components/BookingActions/CustomResourceInfo').then(({ CustomResourceInfo }) => ({
    default: () => <CustomResourceInfo />,
  }))
);

const SpaceChangeProposalRoute = lazy(() =>
  import('@/components/SpaceChangeProposal').then(({ SpaceChangeProposal }) => ({
    default: () => <SpaceChangeProposal />,
  }))
);

const CapacityCheckRoute = lazy(() =>
  import('@/components/CapacityCheck').then(({ CapacityCheck }) => ({
    default: () => <CapacityCheck />,
  }))
);

const EventWriteableRoute = lazy(() =>
  import('@/components/EventWriteable').then(({ EventWriteable }) => ({
    default: () => <EventWriteable />,
  }))
);

const SpaceSuitabilityRoute = lazy(() =>
  import('@/components/SpaceSuitability').then(({ SpaceSuitability }) => ({
    default: () => <SpaceSuitability />,
  }))
);

const EventDetailsRoute = lazy(() =>
  import('@/components/EventDetails').then(({ EventDetails }) => ({
    default: () => <EventDetails />,
  }))
);

const NewEventProposalRoute = lazy(() =>
  import('@/components/NewEventProposal').then(({ NewEventProposal }) => ({
    default: () => <NewEventProposal />,
  }))
);

const LocationPickerRoute = lazy(() =>
  import('@/components/LocationPicker').then(({ LocationPicker }) => ({
    default: () => <LocationPicker />,
  }))
);

const DatePickerRoute = lazy(() =>
  import('@/components/DatePicker').then(({ DatePicker }) => ({
    default: () => <DatePicker />,
  }))
);

const ViewReservationsRoute = lazy(() =>
  import('@/components/ViewReservations/ViewReservations').then(({ ViewReservations }) => ({
    default: () => <ViewReservations />,
  }))
);

const SpaceSuggestionsRoute = lazy(() =>
  import('@/components/SpaceSuggestions').then(({ SpaceSuggestions }) => ({
    default: () => <SpaceSuggestions />,
  }))
);

const CancelBookingsRoute = lazy(() =>
  import('@/components/CancelBookings/CancelBookings').then(({ CancelBookings }) => ({
    default: () => <CancelBookings />,
  }))
);

const EditBookingsRoute = lazy(() =>
  import('@/components/EditBookings/EditBookings').then(({ EditBookings }) => ({
    default: () => <EditBookings />,
  }))
);

const ReleaseBookingsRoute = lazy(() =>
  import('@/components/ReleaseBookings/ReleaseBookings').then(({ ReleaseBookings }) => ({
    default: () => <ReleaseBookings />,
  }))
);

/**
 * Routes array
 * Add new routes here when creating new components
 */
export const routes: Route[] = [
  {
    path: '/desk-info',
    component: DeskInfoRoute,
    componentName: 'DeskInfo',
  },
  {
    path: '/booking-suggestions',
    component: BookingSuggestionsRoute,
    componentName: 'BookingSuggestions',
  },
  {
    path: '/custom-resource-info',
    component: CustomResourceInfoRoute,
    componentName: 'CustomResourceInfo',
  },
  {
    path: '/space-change-proposal',
    component: SpaceChangeProposalRoute,
    componentName: 'SpaceChangeProposal',
  },
  {
    path: '/capacity-check',
    component: CapacityCheckRoute,
    componentName: 'CapacityCheck',
  },
  {
    path: '/check-event-writeable',
    component: EventWriteableRoute,
    componentName: 'EventWriteable',
  },
  {
    path: '/space-suitability',
    component: SpaceSuitabilityRoute,
    componentName: 'SpaceSuitability',
  },
  {
    path: '/event-details',
    component: EventDetailsRoute,
    componentName: 'EventDetails',
  },
  {
    path: '/new-event-proposal',
    component: NewEventProposalRoute,
    componentName: 'NewEventProposal',
  },
  {
    path: '/location-picker',
    component: LocationPickerRoute,
    componentName: 'LocationPicker',
  },
  {
    path: '/date-picker',
    component: DatePickerRoute,
    componentName: 'DatePicker',
  },
  {
    path: '/view-reservations',
    component: ViewReservationsRoute,
    componentName: 'ViewReservations',
  },
  {
    path: '/space-suggestions',
    component: SpaceSuggestionsRoute,
    componentName: 'SpaceSuggestions',
  },
  {
    path: '/cancel-bookings',
    component: CancelBookingsRoute,
    componentName: 'CancelBookings',
  },
  {
    path: '/edit-bookings',
    component: EditBookingsRoute,
    componentName: 'EditBookings',
  },
  {
    path: '/release-bookings',
    component: ReleaseBookingsRoute,
    componentName: 'ReleaseBookings',
  },
];
